import { ref, reactive, computed } from 'vue'

export function useFormValidation() {
  const errors = reactive({})
  const touched = reactive({})
  const loading = ref(false)

  // Validation rules
  const rules = {
    required: (value, fieldName) => {
      if (!value || value.toString().trim() === '') {
        return `${fieldName} is required`
      }
      return null
    },
    
    minLength: (min) => (value, fieldName) => {
      if (value && value.length < min) {
        return `${fieldName} must be at least ${min} characters`
      }
      return null
    },
    
    maxLength: (max) => (value, fieldName) => {
      if (value && value.length > max) {
        return `${fieldName} must be less than ${max} characters`
      }
      return null
    },
    
    email: (value, fieldName) => {
      if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
        return `${fieldName} must be a valid email address`
      }
      return null
    },
    
    url: (value, fieldName) => {
      if (value && !/^https?:\/\/.+/.test(value)) {
        return `${fieldName} must be a valid URL`
      }
      return null
    },
    
    spotifyId: (value, fieldName) => {
      if (value && !/^[a-zA-Z0-9]{22}$/.test(value)) {
        return `${fieldName} must be a valid Spotify ID (22 characters)`
      }
      return null
    },
    
    tidalId: (value, fieldName) => {
      if (value && !/^\d{8,10}$/.test(value.toString())) {
        return `${fieldName} must be 8-10 digits`
      }
      return null
    }
  }

  // Validate a single field
  const validateField = (fieldName, value, fieldRules = [], customFieldName = null) => {
    const displayName = customFieldName || fieldName.charAt(0).toUpperCase() + fieldName.slice(1)
    touched[fieldName] = true
    
    for (const rule of fieldRules) {
      const error = rule(value, displayName)
      if (error) {
        errors[fieldName] = error
        return { isValid: false, error }
      }
    }
    
    // Clear error if validation passes
    delete errors[fieldName]
    return { isValid: true, error: null }
  }

  // Validate entire form
  const validateForm = (formData, validationSchema) => {
    let isFormValid = true
    
    for (const [fieldName, fieldRules] of Object.entries(validationSchema)) {
      const customFieldName = fieldRules.displayName
      const ruleArray = fieldRules.rules || fieldRules
      const result = validateField(fieldName, formData[fieldName], ruleArray, customFieldName)
      if (!result.isValid) {
        isFormValid = false
      }
    }
    
    return isFormValid
  }

  // Clear all errors
  const clearErrors = () => {
    Object.keys(errors).forEach(key => delete errors[key])
    Object.keys(touched).forEach(key => delete touched[key])
  }

  // Clear specific field error
  const clearFieldError = (fieldName) => {
    delete errors[fieldName]
  }

  // Check if field has error
  const hasError = (fieldName) => {
    return !!errors[fieldName] && touched[fieldName]
  }

  // Get field error message
  const getFieldError = (fieldName) => {
    return hasError(fieldName) ? errors[fieldName] : ''
  }

  // Check if form is valid
  const isFormValid = computed(() => {
    return Object.keys(errors).length === 0
  })

  // Check if any field has been touched
  const hasAnyTouched = computed(() => {
    return Object.keys(touched).length > 0
  })

  return {
    errors,
    touched,
    loading,
    rules,
    validateField,
    validateForm,
    clearErrors,
    clearFieldError,
    hasError,
    getFieldError,
    isFormValid,
    hasAnyTouched
  }
}

// Pre-defined validation schemas for common forms
export const albumValidationSchema = {
  spotify_id: {
    displayName: 'Spotify Album ID',
    rules: [
      (value) => value && value.trim() !== '' ? null : 'Spotify Album ID is required',
      (value) => {
        if (value && !/^[a-zA-Z0-9]{22}$/.test(value.trim())) {
          return 'Spotify Album ID must be exactly 22 characters'
        }
        return null
      }
    ]
  },
  artist: {
    displayName: 'Artist',
    rules: [
      (value) => value && value.trim() !== '' ? null : 'Artist is required',
      (value) => value && value.length > 100 ? 'Artist name must be less than 100 characters' : null
    ]
  },
  title: {
    displayName: 'Album Title',
    rules: [
      (value) => value && value.trim() !== '' ? null : 'Album title is required',
      (value) => value && value.length > 200 ? 'Album title must be less than 200 characters' : null
    ]
  },
  spotify_url: {
    displayName: 'Spotify URL',
    rules: [
      (value) => value && value.trim() !== '' ? null : 'Spotify URL is required',
      (value) => {
        if (value && !/^https:\/\/open\.spotify\.com\/album\//.test(value.trim())) {
          return 'Must be a valid Spotify album URL'
        }
        return null
      }
    ]
  },
  tidal_album_id: {
    displayName: 'Tidal Album ID',
    rules: [
      // Tidal ID is optional, so no required validation
      (value) => {
        if (value && !/^\d{8,10}$/.test(value.toString().trim())) {
          return 'Tidal Album ID must be 8-10 digits'
        }
        return null
      }
    ]
  },
  session_id: {
    displayName: 'Session',
    rules: [
      (value) => value ? null : 'Please select a session'
    ]
  },
  tier: {
    displayName: 'Tier',
    rules: [
      (value) => value && value.trim() !== '' ? null : 'Please select a tier'
    ]
  },
  submitted_by: {
    displayName: 'Submitted By',
    rules: [
      (value) => value && value.trim() !== '' ? null : 'Please select who submitted this album'
    ]
  },
  password: {
    displayName: 'Admin Password',
    rules: [
      (value) => value && value.trim() !== '' ? null : 'Admin password is required'
    ]
  },
  cover_url: {
    displayName: 'Album Cover URL',
    rules: [
      (value) => value && value.trim() !== '' ? null : 'Album cover URL is required',
      (value) => {
        if (value && !/^https?:\/\/.+/.test(value.trim())) {
          return 'Album cover URL must be a valid URL'
        }
        return null
      }
    ]
  }
}